import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

const STROKE_COLOR = '#111827';
const STROKE_WIDTH = 2.4;

const getCanvasPoint = (canvas, event) => {
  const rect = canvas.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
};

const configureCanvas = (canvas, height) => {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(Math.floor(rect.width), 1);
  const surfaceHeight = Math.max(Math.floor(height), 1);
  const ratio = window.devicePixelRatio || 1;
  const context = canvas.getContext('2d');

  canvas.width = Math.max(width * ratio, 1);
  canvas.height = Math.max(surfaceHeight * ratio, 1);

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, surfaceHeight);
  context.strokeStyle = STROKE_COLOR;
  context.lineWidth = STROKE_WIDTH;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  return { context, width, height: surfaceHeight };
};

export default function SignaturePadField({
  value,
  onChange,
  label = 'Assinatura do cliente',
  helperText = 'Assine com mouse, touchpad ou dedo diretamente na tela.',
  height = 180,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const redrawCanvas = useCallback(
    (imageDataUrl) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const { context, width, height: surfaceHeight } = configureCanvas(canvas, height);
      if (!imageDataUrl) return;

      const image = new Image();
      image.onload = () => {
        context.drawImage(image, 0, 0, width, surfaceHeight);
      };
      image.src = imageDataUrl;
    },
    [height]
  );

  useEffect(() => {
    redrawCanvas(value);
  }, [redrawCanvas, value]);

  useEffect(() => {
    const handleResize = () => redrawCanvas(value);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawCanvas, value]);

  const getContext = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return null;

    context.strokeStyle = STROKE_COLOR;
    context.lineWidth = STROKE_WIDTH;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    return context;
  };

  const startDrawing = (event) => {
    const canvas = canvasRef.current;
    const context = getContext();
    if (!canvas || !context) return;

    event.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getCanvasPoint(canvas, event);
    canvas.setPointerCapture?.(event.pointerId);

    const point = lastPointRef.current;
    context.beginPath();
    context.moveTo(point.x, point.y);
    context.lineTo(point.x + 0.01, point.y + 0.01);
    context.stroke();
  };

  const draw = (event) => {
    if (!drawingRef.current) return;

    const canvas = canvasRef.current;
    const context = getContext();
    if (!canvas || !context || !lastPointRef.current) return;

    event.preventDefault();
    const point = getCanvasPoint(canvas, event);

    context.beginPath();
    context.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    context.lineTo(point.x, point.y);
    context.stroke();

    lastPointRef.current = point;
  };

  const finishDrawing = () => {
    if (!drawingRef.current) return;

    drawingRef.current = false;
    lastPointRef.current = null;
    if (canvasRef.current) {
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    redrawCanvas('');
    onChange('');
  };

  return (
    <Stack spacing={1}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={clearSignature}>
          Limpar assinatura
        </Button>
      </Stack>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={finishDrawing}
          onPointerLeave={finishDrawing}
          onPointerCancel={finishDrawing}
          sx={{
            display: 'block',
            width: '100%',
            height,
            touchAction: 'none',
            cursor: 'crosshair',
          }}
        />
      </Box>
    </Stack>
  );
}
